/**
 * TagInput - V2 Component für Tag-Eingabe mit Autocomplete
 *
 * @description Tailwind-basierte Tag-Eingabe mit Autocomplete-Suche und Tastaturnavigation.
 * @example
 * <TagInput
 *   tags={selectedTags}
 *   onChange={setSelectedTags}
 *   onCreateTag={handleCreateTag}
 *   placeholder="Tag hinzufügen..."
 * />
 */
import { useState, useRef, useEffect, useCallback, forwardRef } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { searchTags, type Tag } from '@/lib/api/tags';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';

/**
 * TagInput Props Interface
 */
export interface TagInputProps {
  /** Aktuell ausgewählte Tags */
  tags: Tag[];
  /** Callback wenn Tags geändert werden */
  onChange: (tags: Tag[]) => void;
  /** Callback wenn neuer Tag erstellt werden soll */
  onCreateTag?: (name: string) => Promise<Tag | null>;
  /** Placeholder-Text */
  placeholder?: string;
  /** Read-only Modus */
  disabled?: boolean;
  /** Maximale Anzahl Tags */
  maxTags?: number;
  /** Zusätzliche CSS-Klassen */
  className?: string;
}

/**
 * TagInput Component
 */
export const TagInput = forwardRef<HTMLDivElement, TagInputProps>(
  (
    {
      tags,
      onChange,
      onCreateTag,
      placeholder,
      disabled = false,
      maxTags,
      className,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const resolvedPlaceholder = placeholder ?? t('gallery.addTag');
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<Tag[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const debouncedValue = useDebounce(inputValue, 200);

    // Autocomplete-Suche
    useEffect(() => {
      if (!debouncedValue.trim()) {
        setSuggestions([]);
        return;
      }

      const loadSuggestions = async () => {
        setIsLoading(true);
        try {
          const response = await searchTags(debouncedValue, 8);
          if (response.success) {
            const filtered = response.tags.filter(
              (t) => !tags.some((existing) => existing.id === t.id)
            );
            setSuggestions(filtered);
          }
        } catch (err) {
          console.error('Error searching tags:', err);
        } finally {
          setIsLoading(false);
        }
      };

      loadSuggestions();
    }, [debouncedValue, tags]);

    // Klick außerhalb schließt Suggestions
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Tag hinzufügen
    const addTag = useCallback(
      async (tag: Tag) => {
        if (maxTags && tags.length >= maxTags) return;
        if (tags.some((t) => t.id === tag.id)) return;

        onChange([...tags, tag]);
        setInputValue('');
        setSuggestions([]);
        setSelectedIndex(-1);
        inputRef.current?.focus();
      },
      [tags, onChange, maxTags]
    );

    // Neuen Tag erstellen
    const createAndAddTag = useCallback(
      async (name: string) => {
        if (!name.trim() || !onCreateTag) return;
        if (maxTags && tags.length >= maxTags) return;
        if (tags.some((t) => t.name.toLowerCase() === name.toLowerCase())) return;

        setIsLoading(true);
        try {
          const newTag = await onCreateTag(name.trim());
          if (newTag) {
            await addTag(newTag);
          }
        } finally {
          setIsLoading(false);
        }
      },
      [tags, onCreateTag, addTag, maxTags]
    );

    // Tag entfernen
    const removeTag = useCallback(
      (tagId: string) => {
        onChange(tags.filter((t) => t.id !== tagId));
      },
      [tags, onChange]
    );

    // Keyboard Navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && suggestions[selectedIndex]) {
            addTag(suggestions[selectedIndex]);
          } else if (inputValue.trim() && onCreateTag) {
            createAndAddTag(inputValue);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          setSelectedIndex(-1);
          break;
        case 'Backspace':
          if (!inputValue && tags.length > 0) {
            removeTag(tags[tags.length - 1].id);
          }
          break;
      }
    };

    const canAddMore = !maxTags || tags.length < maxTags;

    return (
      <div ref={ref} className={cn('space-y-2', className)}>
        {/* Tag-Badges */}
        <div ref={containerRef} className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium rounded-md text-white"
              style={{ backgroundColor: tag.color || '#6b7280' }}
            >
              {tag.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag.id)}
                  className="ml-1 hover:bg-white/20 rounded p-0.5 transition-colors"
                  aria-label={t('common.remove')}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>

        {/* Input */}
        {canAddMore && !disabled && (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              className={cn(
                'w-full px-3 py-2 text-sm rounded-md',
                'bg-neutral-50',
                'border border-neutral-300',
                'text-neutral-900',
                'placeholder:text-neutral-400',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              placeholder={resolvedPlaceholder}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setShowSuggestions(true);
                setSelectedIndex(-1);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />

            {/* Loading Spinner */}
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
              </div>
            )}

            {/* Autocomplete Dropdown */}
            {showSuggestions && (suggestions.length > 0 || inputValue.trim()) && (
              <div
                className={cn(
                  'absolute z-50 w-full mt-1 py-1 rounded-md shadow-lg',
                  'bg-white',
                  'border border-neutral-200',
                  'max-h-52 overflow-y-auto'
                )}
              >
                {suggestions.map((tag, index) => (
                  <button
                    key={tag.id}
                    type="button"
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm flex items-center gap-2',
                      'hover:bg-neutral-100',
                      'transition-colors',
                      index === selectedIndex && 'bg-primary-50'
                    )}
                    onClick={() => addTag(tag)}
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1 text-neutral-900">
                      {tag.name}
                    </span>
                    <span className="text-xs text-neutral-400">{tag.usage_count}×</span>
                  </button>
                ))}

                {/* Neuen Tag erstellen */}
                {inputValue.trim() &&
                  onCreateTag &&
                  !suggestions.some((s) => s.name.toLowerCase() === inputValue.toLowerCase()) && (
                    <button
                      type="button"
                      className={cn(
                        'w-full px-3 py-2 text-left text-sm flex items-center gap-2',
                        'hover:bg-neutral-100',
                        'transition-colors text-green-600',
                        selectedIndex === suggestions.length && 'bg-green-50'
                      )}
                      onClick={() => createAndAddTag(inputValue)}
                    >
                      <Plus className="w-4 h-4" />
                      <span>"{inputValue.trim()}"</span>
                    </button>
                  )}

                {!suggestions.length && !inputValue.trim() && (
                  <div className="px-3 py-2 text-sm text-neutral-400">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Max Tags Hinweis */}
        {maxTags && (
          <p className="text-xs text-neutral-500">
            {tags.length}/{maxTags} Tags
          </p>
        )}
      </div>
    );
  }
);

TagInput.displayName = 'TagInput';
